import { fetchAuthSession } from '@aws-amplify/auth';
import { MILLISECONDS } from '@chiroup/core/constants/time';
import React, { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReadyState } from 'react-use-websocket/dist/lib/constants';
import { WebSocketMessage } from 'react-use-websocket/dist/lib/types';
import { useWebSocket } from 'react-use-websocket/dist/lib/use-websocket';
import { MeContext } from './me.context';
import { WSConnectionStatusContext, WSContext } from './ws.context';

type Props = {
  children: React.ReactNode;
};

// Refresh the access token only when it's within this window of expiring.
// Mirrors the buffer used in useCollaborativeEncounter.ts so both WS layers
// share the same caching policy.
const TOKEN_REFRESH_BUFFER_WINDOW_MS = MILLISECONDS.inSeconds(60);

const WebsocketContext = ({ children }: Props) => {
  const { sessionId, me, selectedLocationFull } = use(MeContext);
  const [subscriptions, setSubscriptions] = useState<{
    [key: string]: Array<(message: WebSocketMessage) => void>;
  }>({});
  // Cache the access token across reconnects to avoid a fetchAuthSession storm
  // (and the resulting Amplify refresh-token rotation race) on network flap.
  const tokenCacheRef = useRef<{ token: string; expiresAt: number } | null>(null);
  const connectionOpenedRef = useRef(false);
  const forceRefreshOnNextConnectRef = useRef(false);
  const forcedRefreshAttemptedRef = useRef(false);
  const getSocketUrl = useCallback(async () => {
    const cached = tokenCacheRef.current;
    const shouldForceRefresh = forceRefreshOnNextConnectRef.current;
    forceRefreshOnNextConnectRef.current = false;

    if (
      !shouldForceRefresh &&
      cached &&
      cached.expiresAt - Date.now() > TOKEN_REFRESH_BUFFER_WINDOW_MS
    ) {
      return `${import.meta.env.VITE_WEBSOCKET_URL}?token=${cached.token}&sessionId=${sessionId}`;
    }

    const authSession = shouldForceRefresh
      ? await fetchAuthSession({ forceRefresh: true })
      : await fetchAuthSession();
    const { accessToken } = authSession.tokens ?? {};
    const tokenString = accessToken?.toString() ?? '';
    const tokenExpiration = accessToken?.payload?.exp;

    if (tokenString && typeof tokenExpiration === 'number') {
      tokenCacheRef.current = {
        token: tokenString,
        expiresAt: tokenExpiration * MILLISECONDS.inSeconds(1),
      };
    }

    return `${import.meta.env.VITE_WEBSOCKET_URL}?token=${tokenString}&sessionId=${sessionId}`;
  }, [sessionId]);

  const { readyState, sendJsonMessage } = useWebSocket<any | null>(getSocketUrl, {
    share: true,
    shouldReconnect: () => {
      const connectionOpened = connectionOpenedRef.current;
      connectionOpenedRef.current = false;

      if (!connectionOpened && !forcedRefreshAttemptedRef.current) {
        tokenCacheRef.current = null;
        forceRefreshOnNextConnectRef.current = true;
        forcedRefreshAttemptedRef.current = true;
      }

      return true;
    },
    retryOnError: true,
    reconnectAttempts: 100,
    reconnectInterval: atmpt => Math.min(Math.pow(2, atmpt) * 1000, 60000),
    onMessage: message => {
      const messageData = JSON.parse(message.data || '{}');
      const { subscription, ...data } = messageData;

      const callbacks = subscriptions[subscription];

      if (callbacks) {
        callbacks.forEach(callback => callback(data));
      }
    },
    onOpen: () => {
      connectionOpenedRef.current = true;
      forceRefreshOnNextConnectRef.current = false;
      forcedRefreshAttemptedRef.current = false;
    },
    onError: _event => {},
    onClose: _event => {},
  });

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];

  const addSubscription = useCallback(
    (subscription: string, callback: (message: WebSocketMessage) => void) => {
      setSubscriptions(prev => {
        const callbacks = prev[subscription] || [];
        if (callbacks.includes(callback)) return prev;

        return {
          ...prev,
          [subscription]: [...callbacks, callback],
        };
      });
    },
    []
  );

  const removeSubscription = useCallback(
    (subscription: string, callback: (message: WebSocketMessage) => void) => {
      setSubscriptions(prev => {
        const callbacks = (prev[subscription] || []).filter(item => item !== callback);
        if (callbacks.length) return { ...prev, [subscription]: callbacks };

        const { [subscription]: _, ...rest } = prev;
        return rest;
      });
    },
    []
  );

  useEffect(() => {
    if (readyState === 1) {
      const subscriptionKeys = Object.keys(subscriptions);

      try {
        sendJsonMessage({
          action: 'subscriptions',
          sessionId,
          userId: me?.ID,
          clinicId: me?.selectedClinic?.ID,
          locationId: selectedLocationFull?.ID,
          subscriptions: subscriptionKeys,
        });
      } catch {
        // WebSocket may close between readyState check and sendJsonMessage —
        // transient timing issue, resolves on reconnect. Not worth reporting.
      }
    }
  }, [
    readyState,
    sessionId,
    subscriptions,
    me?.ID,
    me?.selectedClinic?.ID,
    selectedLocationFull?.ID,
    sendJsonMessage,
  ]);

  const contextValue = useMemo(
    () => ({ addSubscription, removeSubscription }),
    [addSubscription, removeSubscription]
  );

  return (
    <WSContext.Provider value={contextValue}>
      <WSConnectionStatusContext.Provider value={connectionStatus}>
        {children}
      </WSConnectionStatusContext.Provider>
    </WSContext.Provider>
  );
};

export default WebsocketContext;
